import axios, { type InternalAxiosRequestConfig } from 'axios';
import { store } from '@/store';
import { logout } from '@/store/slices/authSlice';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8081/api/v1';
const CSRF_COOKIE_NAME = 'csrfToken';
const CSRF_HEADER_NAME = 'X-CSRF-Token';
const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);
const CSRF_EXEMPT_UNSAFE_PATHS = new Set([
  '/auth/login',
  '/auth/demo-login',
  '/auth/register',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/signup',
  '/auth/verify-email',
  '/auth/resend-verification',
  '/auth/mfa/login-verify',
  '/invitations/validate-token',
  '/invitations/accept',
  '/signup',
  '/contact/sales-inquiry',
  '/telemetry/beacon',
  // SSO discovery on the login page (launch-readiness S13; exempt server-side too).
  '/sso/discover',
]);

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${encodeURIComponent(name)}=`;
  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  if (!cookie) return null;
  return decodeURIComponent(cookie.slice(prefix.length));
}

// Browser auth is cookie-only. withCredentials sends the httpOnly access cookie
// while keeping long-lived JWTs out of localStorage and JavaScript-readable
// request headers.
export const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

let cachedCsrfToken: string | null = null;
let pendingCsrfToken: Promise<string | null> | null = null;

function normalizeApiPath(url: string | undefined): string {
  if (!url) return '/';
  let path = url.split('?')[0];

  if (/^https?:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname;
      const apiBasePath = new URL(API_URL).pathname.replace(/\/$/, '');
      if (apiBasePath && path.startsWith(`${apiBasePath}/`)) {
        path = path.slice(apiBasePath.length);
      }
    } catch {
      return url.split('?')[0];
    }
  }

  if (path.startsWith('/api/v1/')) {
    path = path.slice('/api/v1'.length);
  }
  return path || '/';
}

function isCsrfExempt(configUrl: string | undefined): boolean {
  return CSRF_EXEMPT_UNSAFE_PATHS.has(normalizeApiPath(configUrl));
}

function isAuthRedirectExempt(configUrl: string | undefined): boolean {
  const path = normalizeApiPath(configUrl);
  return path.startsWith('/auth/') || CSRF_EXEMPT_UNSAFE_PATHS.has(path);
}

function setCsrfHeader(config: InternalAxiosRequestConfig, token: string) {
  const headers = config.headers as any;
  if (headers && typeof headers.set === 'function') {
    headers.set(CSRF_HEADER_NAME, token);
  } else {
    config.headers = {
      ...(headers || {}),
      [CSRF_HEADER_NAME]: token,
    } as typeof config.headers;
  }
}

async function fetchCsrfToken(): Promise<string | null> {
  if (!pendingCsrfToken) {
    pendingCsrfToken = apiClient
      .get('/auth/csrf-token')
      .then((res) => {
        const token = res.data?.data?.csrfToken ?? res.data?.csrfToken;
        cachedCsrfToken = typeof token === 'string' && token.length > 0 ? token : null;
        return cachedCsrfToken;
      })
      .finally(() => {
        pendingCsrfToken = null;
      });
  }
  return pendingCsrfToken;
}

apiClient.interceptors.request.use(async (config) => {
  const method = (config.method || 'get').toLowerCase();
  if (!UNSAFE_METHODS.has(method) || isCsrfExempt(config.url)) return config;

  const readableCookieToken = readCookie(CSRF_COOKIE_NAME);
  if (readableCookieToken) cachedCsrfToken = readableCookieToken;
  const csrfToken = readableCookieToken ?? cachedCsrfToken ?? await fetchCsrfToken();
  if (!csrfToken) return config;

  setCsrfHeader(config, csrfToken);
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    const isAxiosError =
      typeof error === 'object' &&
      error !== null &&
      'response' in error &&
      'config' in error;

    if (isAxiosError) {
      const e = error as { response?: { status: number }; config?: { url?: string } };
      if (e.response?.status === 401 && !isAuthRedirectExempt(e.config?.url)) {
        store.dispatch(logout());
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  },
);
